import { useIntl } from 'react-intl';

import { useAppStateStatus } from 'components/ota-update-alert/use-app-state-status';
import { IOperatingHours, IRestaurantNode } from 'generated/rbi-graphql';
import { useConfigValue } from 'hooks/configs/use-config-value';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { ServiceMode } from 'state/service-mode';
import {
  isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours,
  readableCloseHourToday,
} from 'utils/restaurant';
import { TWELVE_HOUR_TIME_PARSE_FORMAT } from 'utils/restaurant/constants';

const getOperatingHours = (restaurant: IRestaurantNode) => (serviceMode: ServiceMode) => {
  switch (serviceMode) {
    case ServiceMode.DRIVE_THRU:
      return restaurant.driveThruHours;
    case ServiceMode.CURBSIDE:
      return restaurant.curbsideHours;
    default:
      return restaurant.diningRoomHours;
  }
};

const isWithinOneHourOfClose = (hours: IOperatingHours) => {
  return !isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours(hours, new Date(Date.now()), 60);
};
interface IOpenClosedText {
  isOpen: boolean;
  text: string;
}

export const useOpenClosedText = (restaurant: IRestaurantNode): IOpenClosedText => {
  const timeFormatConfig = useConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });
  // This is used to force the component to re-render in case the user focus this screen again. This way we'll have updated restaurant open/closed status info.
  const appStateStatus = useAppStateStatus();

  const { availablePickupServiceModes } = useServiceModeStatus(restaurant, true, appStateStatus);
  const { formatMessage } = useIntl();

  // Get the open hours for the pickup service mode that is open and not disabled.
  const [operatingHours] = availablePickupServiceModes.map(getOperatingHours(restaurant));

  if (!operatingHours) {
    return { isOpen: false, text: formatMessage({ id: 'closed' }) };
  }

  if (isWithinOneHourOfClose(operatingHours)) {
    return {
      isOpen: true,
      text: formatMessage(
        { id: 'closesAt' },
        { time: readableCloseHourToday(operatingHours, timeFormatConfig) }
      ),
    };
  }

  return { isOpen: true, text: formatMessage({ id: 'open' }) };
};
